import { useState } from 'react';
import clsx from 'clsx';
import { markOrderPickedUp, type MarkPickedUpMode } from '../../lib/mark-picked-up';

interface MarkPickedUpDialogProps {
  orderId: string;
  orderNumber: string;
  onClose: () => void;
  onSaved: () => void;
}

function todayIso(): string {
  // Lokale datum als YYYY-MM-DD (geen UTC-shift).
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 10);
}

const MODE_OPTIONS: {
  value: MarkPickedUpMode;
  label: string;
  hint: string;
}[] = [
  {
    value: 'chauffeur',
    label: 'Chauffeur nam het mee',
    hint: 'Bijv. meegenomen bij een aflevering → Opgehaald',
  },
  {
    value: 'klant',
    label: 'Klant heeft het gebracht',
    hint: 'Klant kwam het zelf langsbrengen → Zelf gebracht',
  },
];

export function MarkPickedUpDialog({
  orderId,
  orderNumber,
  onClose,
  onSaved,
}: MarkPickedUpDialogProps) {
  const [mode, setMode] = useState<MarkPickedUpMode>('chauffeur');
  const [pickupDate, setPickupDate] = useState<string>(todayIso());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isKlant = mode === 'klant';
  const today = todayIso();
  const dateValid = !!pickupDate && pickupDate <= today;
  const canSubmit = !submitting && dateValid;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await markOrderPickedUp({
        orderId,
        mode,
        pickupDate: pickupDate || undefined,
        notes: notes.trim() || undefined,
      });
      if (!result.ok) {
        setError(result.error || 'Markeren mislukt');
        return;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Markeren mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Kleed al binnen markeren</h2>
          <p className="text-xs text-slate-500 mt-1">Order {orderNumber}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-slate-700">
            Gebruik dit voor kleden die al in de wasserij liggen, zonder ophaalrit.
            De kleden gaan direct naar{' '}
            <span className="font-medium">Klaar voor Voorinspectie</span>; er wordt
            géén ophaalvoorstel gedaan en er gaan geen ophaal-reminders uit.
          </p>

          <div>
            <span className="text-sm text-slate-700">Hoe is het binnengekomen?</span>
            <div className="mt-1.5 space-y-1.5">
              {MODE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={clsx(
                    'flex items-start gap-2 rounded border px-3 py-2 cursor-pointer',
                    mode === opt.value
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-300 hover:border-slate-400'
                  )}
                >
                  <input
                    type="radio"
                    name="mark-picked-up-mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={() => setMode(opt.value)}
                    disabled={submitting}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm text-slate-900">{opt.label}</span>
                    <span className="block text-xs text-slate-500">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-sm text-slate-700">
              {isKlant ? 'Ontvangen op' : 'Opgehaald op'}
            </span>
            <input
              type="date"
              value={pickupDate}
              max={today}
              onChange={(e) => setPickupDate(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
            {pickupDate && !dateValid && (
              <p className="text-xs text-amber-600 mt-1">
                Datum mag niet in de toekomst liggen.
              </p>
            )}
          </label>

          <label className="block">
            <span className="text-sm text-slate-700">Notitie (optioneel)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              placeholder={
                isKlant
                  ? 'Bv. klant langsgebracht aan de balie'
                  : 'Bv. meegenomen bij aflevering WB-309xxx'
              }
              disabled={submitting}
            />
          </label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              !canSubmit
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            )}
          >
            {submitting
              ? 'Markeren…'
              : isKlant
              ? 'Markeer als zelf gebracht'
              : 'Markeer als opgehaald'}
          </button>
        </div>
      </div>
    </div>
  );
}
